/**
 * WERSJA „OBECNA” (produkcja 0.31.3) — „Zlecenia dla tego pojazdu” na karcie pojazdu, kopia z `main`
 * (`git show origin/main:apps/app/src/admin/fleet/CarOrderSuggestions.tsx`), 12.10.2026.
 *
 * Decyzja użytkownika 12.10.2026: „obecna” wersja aplikacji ma zachowywać
 * się dokładnie jak produkcja, a zmiany z develop są za przełącznikami.
 * Z tego pliku korzysta karta pojazdu przy wyłączonym przełączniku
 * `vehicleCardPrototype` (`fleet/CarShow.tsx`). Zmieniamy go tylko po to,
 * żeby wpiąć inne przełączniki (sekcja domyślnie niewidoczna) — przy
 * wszystkich wyłączonych ekran ma być taki jak na produkcji. Po decyzji
 * „wchodzi” albo „odrzucone” katalog `legacy/` znika razem z przełącznikiem.
 */
import { Fragment, useState } from 'react';
import { Link, useNotify, useRecordContext, type RaRecord } from 'react-admin';
import AddIcon from '@mui/icons-material/Add';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import axiosNest from '../../../api/axiosNest';
import { SectionCard } from '../../../shared/Panel';
import { addCandidate, toErrorMessage } from '../../userOffers/orderActionsApi';
import { DeliveryCell, type DeliveryEstimate } from '../../userOffers/DeliveryCell';
import { WarningsList } from '../../userOffers/WarningsList';
import { BasisList, BasisToggle, type SuggestionBasisItem } from '../../userOffers/SuggestionBasis';
import { FreeFromCell, WorkReserveCell, WorkReserveHeader } from '../../userOffers/suggestionColumns';
import { fullDateTime, shortDateTime } from '../../../shared/dateTime';
import { plural } from '../../../shared/plural';
import { recordScoped, useStillMounted } from '../../../shared/recordScoped';
import { TABLE_SCROLL_SX } from '../../../shared/tableScroll';
import { isOrderVehicle, vehicleTypeLabel } from '../vehicleTypes';
import { AccessCostCell } from '../../userOffers/AccessCostCell';
import { MarginCell } from '../../userOffers/MarginCell';
import { ReleaseVariantButton } from '../../userOffers/ReleaseVariantButton';
import type { AccessCost, CandidateMargin, WorkReserve } from '../../userOffers/suggestionsApi';
import { AccessDistanceCell, OrderRouteNote } from '../../userOffers/RouteFigureCell';
import { accessSourceNote, isMeasured, type RouteFigure } from '../../userOffers/routeFigure';
import { useHasPermission } from '../../PermissionGate';
import { VARIANT_TOOLTIP } from '../../userOffers/VehicleSuggestions';

/** Liczba z jednostką i data nie łamią się — „+100,8 / h” czyta się jak dwie wartości. */
const NOWRAP = { whiteSpace: 'nowrap' } as const;

/** Te same węższe marginesy i wyrównanie do góry co w podpowiedzi pojazdów. */
const TABLE_SX = {
  '& .MuiTableCell-root': { px: 1 },
  '& .MuiTableCell-body': { verticalAlign: 'top' },
  '& .MuiTableCell-head': { verticalAlign: 'bottom' },
} as const;

/** Liczba kolumn tabeli — wiersz „Na jakiej podstawie” zajmuje całą szerokość. */
const COLUMNS = 9;

/**
 * Kolumna akcji jak w rekomendacjach pojazdów (`VehicleSuggestions`):
 * „Wariant” (przycisk albo znacznik), pod nim ikony z podpisem w dymku —
 * w pionie, wyrównane do prawej, napisy się nie łamią (06.10.2026). Dotąd
 * znacznik „wariant już dodany” i przycisk „Usuń wariant” słowami dawały
 * kolumnę ok. 145 px, a tabela przy oknie 1366 px przewijała się w bok
 * i ucinała przyciski („USU”, „DOD”).
 */
const ACTIONS_COLUMN_SX = {
  alignItems: 'flex-end',
  '& > *': { flexShrink: 0 },
} as const;
const ACTIONS_ICONS_SX = { justifyContent: 'flex-end' } as const;

/** Dymek przy znaczniku „Wariant”. */
export const ALREADY_VARIANT_TOOLTIP = 'Pojazd jest już wariantem tego zlecenia.';

/**
 * Miejsce relacji: kraj z pierwszym słowem nazwy trzyma się razem („DE
 * ZEHDENICK”), a dłuższa nazwa łamie się po słowach — w jednej linii
 * „PL GORZÓW WIELKOPOLSKI” poszerzało kolumnę „Relacja” (06.10.2026).
 */
const placeText = (place: string) => place.replace(' ', '\u00a0');

/**
 * Skąd liczony dolot do zlecenia — GPS z czasem odczytu albo rozładunek
 * obsadzenia. Pozycja zależy od zlecenia (28.09.2026): po przyszłym
 * obsadzeniu auto rusza z jego rozładunku, przed nim — z GPS. Na ekranie
 * w kolumnie „Wolny od” (`suggestionColumns.tsx`, 01.10.2026).
 */
export interface SuggestionPosition {
  zrodlo: string;
  fromOrder: string | null;
  /** Id zlecenia `fromOrder` — link do jego karty. Brak = starsze API. */
  fromOrderId?: number | null;
  measuredAt: string | null;
  freeFrom: string;
}

export interface OrderSuggestion {
  userOfferId: number;
  externalId: string | null;
  planningStatus: string;
  from: string;
  to: string;
  fromDate: string;
  accessDistance: { roadKm: number; straightKm: number; multiplier: number };
  /**
   * Dolot z podpisanym źródłem — trasa z map HERE albo szacunek, z rachunkiem
   * poglądowym (06.10.2026). Brak = starsze API, zostaje `accessDistance`.
   */
  accessRoute?: RouteFigure;
  /** Trasa zlecenia z podpisanym źródłem; `null` = nie liczymy. Brak = starsze API. */
  route?: RouteFigure | null;
  /** Pozycja, od której liczony dolot tego zlecenia; starsza odpowiedź API może jej nie mieć. */
  position?: SuggestionPosition;
  spareHours: number;
  onTime: { willMakeIt: boolean | null; reason: string | null; certain: boolean };
  /** Szacowany przyjazd na rozładunek; starsza odpowiedź API może go nie mieć. */
  delivery?: DeliveryEstimate;
  /**
   * Zapas czasu pracy kierowcy po wykonaniu zlecenia (06.10.2026) — ta sama
   * reguła co przy podpowiedzi pojazdów, więc ta sama para auto–zlecenie ma
   * w obu kierunkach ten sam zapas. Brak = starsze API.
   */
  workReserve?: WorkReserve;
  ostrzezenia: string[];
  alreadyCandidate: boolean;
  /**
   * Koszt dolotu w PLN (omówienie 01.10.2026: „ilość kilometrów, ilość
   * złotówek”) — ta sama reguła co przy podpowiedzi pojazdów, stawki spółki
   * pojazdu, kurs NBP z dnia załadunku. Brak = starsze API.
   */
  accessCost?: AccessCost;
  /**
   * Marża z pasmem alertu — ta sama reguła co przy podpowiedzi pojazdów:
   * cena zlecenia w PLN (kurs NBP z dnia załadunku) minus koszt dolotu
   * i trasy. Alert, nie ranking. Brak = starsze API.
   */
  margin?: CandidateMargin;
  /**
   * Na jakiej podstawie — te same punkty co przy podpowiedzi pojazdów dla tej
   * samej pary auto–zlecenie (`suggestion-basis.ts`). Brak = starsze API.
   */
  basis?: SuggestionBasisItem[];
}

export interface OrderSuggestionsResult {
  refusal: string | null;
  orders: OrderSuggestion[];
  skippedSummary: Record<string, number>;
  /** Podpisy powodów z `skippedSummary` po polsku, z API (07.10.2026); brak = starsze API. */
  skippedLabels?: Record<string, string>;
  /** Pozycja po bieżącym kursie — dolot każdego zlecenia liczy się od jego `position`. */
  position: SuggestionPosition | null;
  horizonDays: number;
}

export async function fetchOrderSuggestions(carId: number | string): Promise<OrderSuggestionsResult> {
  const { data } = await axiosNest.get(`/user-offers/for-car/${carId}/suggestions`);
  return data;
}

/** Tytuł sekcji (poprawa brzmienia 01.10.2026: rekomendacja, nie „podpowiedź”). */
const TITLE = 'Rekomendowane zlecenia dla pojazdu';

/**
 * „Nie pokazano 3 zleceń: kolizja z innym zleceniem tego pojazdu (2), …” albo
 * `null`. Powody z API (`ORDER_SKIP_REASON` w `order-suggestions.ts`) są
 * kluczami odpowiedzi; podpisy przychodzą z API obok nich (`skippedLabels`,
 * 07.10.2026: „mapa etykiet po stronie API”). Starsze API — sam klucz.
 */
function skippedOrdersText(
  summary: Record<string, number>,
  labels: Record<string, string> = {},
): string | null {
  const entries = Object.entries(summary);
  if (entries.length === 0) return null;
  const total = entries.reduce((sum, [, n]) => sum + n, 0);
  const reasons = entries.map(([reason, n]) => `${labels[reason] ?? reason} (${n})`).join(', ');
  return `Nie pokazano ${plural(total, 'zlecenia', 'zleceń', 'zleceń')}: ${reasons}.`;
}

/** „w ciągu 7 dni” — z odmianą, bo horyzont to parametr. */
const withinDays = (n: number) => `w ciągu ${plural(n, 'dnia', 'dni', 'dni')}`;

/** Skąd liczony dolot — jedno zdanie pod tabelą. */
function accessOriginText(result: OrderSuggestionsResult): string {
  if (result.orders.some((o) => o.position)) {
    return (
      'Liczymy go od miejsca, w którym pojazd będzie przed załadunkiem danego zlecenia, czyli po ' +
      'zakończeniu wcześniejszych obsadzeń, także zaplanowanych. Źródło pozycji podaje kolumna „Wolny od”.'
    );
  }
  if (result.position?.zrodlo === 'TELEMATYKA') return 'Liczymy go od ostatniej pozycji GPS.';
  if (result.position?.fromOrder) return `Liczymy go od rozładunku zlecenia ${result.position.fromOrder}.`;
  return 'Liczymy go od ostatniej znanej pozycji pojazdu.';
}

/**
 * Zlecenia dla tego pojazdu — podpowiedź w drugą stronę (FTL V2.1 §12:
 * „pojazd → zlecenia”, Etap 1: rekomendacje dwukierunkowe).
 *
 * Te same zasady co przy podpowiedzi pojazdów: kolejność po dolocie
 * (szacunek), składniki osobno, żadnej oceny złożonej. Odpadają wyłącznie
 * zlecenia kolidujące z obsadzeniami auta i z załadunkiem przed jego
 * zwolnieniem — i mówimy, ile ich było. „Dodaj wariant” zakłada kandydaturę
 * przy zleceniu; obsadzenie zostaje osobną decyzją przy zleceniu.
 *
 * Omówienie 01.10.2026 [1:40:10–1:42:38]: przy każdym zleceniu kilometry
 * i złotówki — koszt dolotu pod dolotem, jak w drugą stronę (składnik, nie
 * ranking: kolejność zostaje po dolocie) — oraz „Usuń wariant” przy
 * zleceniu, do którego auto już jest wariantem. Usunięcie to zwolnienie
 * kandydatury z powodem (`ReleaseVariantButton`, wpis w dzienniku
 * zlecenia); przycisk widzi tylko rola z `FTL_PLANNING`, a API i tak
 * odmawia bez niego (403).
 */
const CarOrderSuggestionsPanel = () => {
  const record = useRecordContext<RaRecord>();
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const canPlan = useHasPermission('FTL_PLANNING');
  const [result, setResult] = useState<OrderSuggestionsResult | null>(null);
  const [busy, setBusy] = useState(false);
  // „Na jakiej podstawie” — rozwinięte uzasadnienie jednego zlecenia naraz.
  const [basisFor, setBasisFor] = useState<number | null>(null);
  if (!record) return null;

  // Naczepa i wózek nie stają pod zlecenie — API odmawia im podpowiedzi
  // (404), więc zamiast przycisku, który kończy się błędem, mówimy, dla
  // kogo ta sekcja jest.
  if (!isOrderVehicle(record.carType)) {
    return (
      <SectionCard title={TITLE}>
        <Typography variant="body2" color="text.secondary">
          Rekomendacje zleceń dotyczą tylko pojazdów wykonujących przewozy, a nie naczep i wózków
          widłowych. Typ tego pojazdu: {vehicleTypeLabel(record.carType)}.
        </Typography>
      </SectionCard>
    );
  }

  // `readOnly`: błąd odczytu dla auta, z którego planista już przeszedł na
  // inne, milczy; błąd zapisu (wariant) mówimy zawsze — to jego akcja.
  const run = async (action: () => Promise<unknown>, readOnly = false) => {
    setBusy(true);
    try {
      await action();
    } catch (e) {
      if (!readOnly || stillMounted()) notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };
  const load = () => void run(async () => setResult(await fetchOrderSuggestions(record.id)), true);
  // Po usunięciu wariantu — świeża lista (zlecenie traci „wariant już dodany”).
  const reload = () => run(async () => setResult(await fetchOrderSuggestions(record.id)), true);
  const vehicleName = String(record.registrationNumber ?? record.name ?? `#${record.id}`);
  const addVariant = (o: OrderSuggestion) =>
    void run(async () => {
      await addCandidate(o.userOfferId, Number(record.id), { origin: 'order_suggestion' });
      notify(`Pojazd został dodany jako wariant do zlecenia ${o.externalId ?? `#${o.userOfferId}`}.`, {
        type: 'success',
      });
      setResult(await fetchOrderSuggestions(record.id));
    });

  const skipped = result ? skippedOrdersText(result.skippedSummary, result.skippedLabels) : null;
  // Choć jeden dolot z map: nagłówek bez „(szacunek)”, źródło w każdej
  // komórce — inaczej szacunek wyglądałby jak trasa z map.
  const measuredAccess = result?.orders.some((o) => isMeasured(o.accessRoute)) ?? false;
  const accessNote = result
    ? accessSourceNote(
        result.orders.map((o) => o.accessRoute),
        result.orders.map((o) => o.route),
      )
    : null;
  // Opłaty drogowe z HERE w koszcie (06.10.2026) — zdanie o koszcie mówi
  // o nich tylko wtedy, gdy API je podaje.
  const withTolls =
    result?.orders.some((o) => o.accessRoute?.source === 'here' && o.accessRoute.tolls !== undefined) ??
    false;

  return (
    <SectionCard title={TITLE}>
      <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: 'center' }}>
        <Button size="small" variant="outlined" disabled={busy} onClick={load}>
          {result === null ? 'Pokaż rekomendowane zlecenia' : 'Przelicz ponownie'}
        </Button>
        {busy && <CircularProgress size={18} />}
      </Stack>

      {result?.refusal && <Alert severity="info">{result.refusal}</Alert>}

      {result && !result.refusal && result.orders.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          Wśród zleceń bez przypisanego pojazdu z załadunkiem {withinDays(result.horizonDays)} nie ma takich,
          które ten pojazd mógłby obsłużyć.
        </Typography>
      )}

      {result && result.orders.length > 0 && (
        <Box sx={TABLE_SCROLL_SX} data-testid="order-suggestions-scroll">
          <Table size="small" sx={TABLE_SX}>
            <TableHead>
              <TableRow>
                <TableCell>Zlecenie</TableCell>
                <TableCell>Relacja</TableCell>
                <TableCell>Załadunek</TableCell>
                {/* Nagłówki opisowe łamią się po słowach — w jednej linii
                    „Dolot i koszt (szacunek)” i „Rozładunek (szacunek)” były
                    szersze niż liczby pod nimi (06.10.2026). */}
                <TableCell align="right">
                  {measuredAccess ? 'Dolot i koszt' : 'Dolot i koszt (szacunek)'}
                </TableCell>
                <TableCell sx={NOWRAP}>Wolny od</TableCell>
                <TableCell align="right">
                  <WorkReserveHeader />
                </TableCell>
                <TableCell>Rozładunek (szacunek)</TableCell>
                {/* 140 px: przy 160 tabela nie mieściła się w karcie przy oknie
                    1366 px (06.10.2026) — uwagi łamią się na więcej linii. */}
                <TableCell sx={{ minWidth: 140 }}>Uwagi</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {result.orders.map((o) => (
                <Fragment key={o.userOfferId}>
                  <TableRow hover>
                    <TableCell sx={NOWRAP}>
                      <Link to={`/user-offers/${o.userOfferId}/show`}>{o.externalId ?? `#${o.userOfferId}`}</Link>
                    </TableCell>
                    <TableCell>
                      {/* Łamiemy między miejscami i między słowami nazwy, nie
                          w środku „DE ZEHDENICK” (`placeText`); strzałka zostaje
                          przy miejscu załadunku. */}
                      <span data-testid="relation-from">{`${placeText(o.from)}\u00a0→`}</span>{' '}
                      <span data-testid="relation-to">{placeText(o.to)}</span>
                      {/* Trasa zlecenia pod relacją (06.10.2026): km ze źródłem,
                          szacunek poglądowy w dymku. */}
                      <OrderRouteNote route={o.route} compact />
                    </TableCell>
                    <TableCell sx={NOWRAP}>
                      <Tooltip title={fullDateTime(o.fromDate)}>
                        <span>{shortDateTime(o.fromDate)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <AccessDistanceCell
                        route={o.accessRoute}
                        legacy={o.accessDistance}
                        showSource={measuredAccess}
                        wrapSource
                      />
                      {/* Koszt pod dolotem, jak w podpowiedzi pojazdów: składnik,
                          nie osobna kolumna do sortowania. */}
                      <AccessCostCell cost={o.accessCost} compact route={o.accessRoute} />
                      {/* Marża i alert marży pod kosztem — ten sam rachunek,
                          jak w podpowiedzi pojazdów; alert, nie ranking. */}
                      <MarginCell margin={o.margin} access={o.accessRoute} route={o.route} wrap />
                    </TableCell>
                    {/* Od kiedy i skąd — ta sama kolumna co przy podpowiedzi
                        pojazdów (omówienie 01.10.2026); wcześniej podpis pod dolotem. */}
                    <TableCell>
                      <FreeFromCell position={o.position} />
                    </TableCell>
                    {/* Zapas czasu pracy po zleceniu (06.10.2026) — ta sama
                        komórka co w rekomendacjach pojazdów: liczba z dymkiem,
                        jak liczona; brak danych to „—” z powodem. */}
                    <TableCell align="right" data-testid="spare-cell">
                      <WorkReserveCell reserve={o.workReserve} />
                    </TableCell>
                    <TableCell>
                      <DeliveryCell delivery={o.delivery} />
                    </TableCell>
                    <TableCell>
                      <WarningsList warnings={o.ostrzezenia} />
                    </TableCell>
                    <TableCell align="right">
                      {/* Akcje w pionie (`ACTIONS_COLUMN_SX`): „Wariant” — znacznik,
                          gdy auto już nim jest, inaczej przycisk dodania — a pod
                          nim ikony z podpisem w dymku: podstawy i usunięcie
                          wariantu. */}
                      <Stack
                        direction="column"
                        spacing={0.5}
                        useFlexGap
                        sx={ACTIONS_COLUMN_SX}
                        data-testid="order-actions"
                      >
                        {o.alreadyCandidate ? (
                          <Tooltip title={ALREADY_VARIANT_TOOLTIP} describeChild placement="top">
                            <Chip
                              size="small"
                              color="primary"
                              variant="outlined"
                              label="Wariant"
                              data-testid="variant-chip"
                            />
                          </Tooltip>
                        ) : (
                          // Owijka, bo zablokowany przycisk nie pokazałby dymka.
                          <Tooltip title={VARIANT_TOOLTIP} describeChild placement="top">
                            <span>
                              <Button
                                size="small"
                                startIcon={<AddIcon />}
                                aria-label="Dodaj wariant"
                                disabled={busy}
                                sx={NOWRAP}
                                onClick={() => addVariant(o)}
                              >
                                Wariant
                              </Button>
                            </span>
                          </Tooltip>
                        )}
                        <Stack
                          direction="row"
                          spacing={0.5}
                          useFlexGap
                          sx={ACTIONS_ICONS_SX}
                          data-testid="order-icons"
                        >
                          {o.basis && o.basis.length > 0 && (
                            <BasisToggle
                              open={basisFor === o.userOfferId}
                              onToggle={() => setBasisFor((p) => (p === o.userOfferId ? null : o.userOfferId))}
                            />
                          )}
                          {o.alreadyCandidate && canPlan && (
                            <ReleaseVariantButton
                              userOfferId={o.userOfferId}
                              carId={Number(record.id)}
                              registrationNumber={`${vehicleName} przy zleceniu ${o.externalId ?? `#${o.userOfferId}`}`}
                              label="Usuń wariant"
                              iconOnly
                              onReleased={reload}
                            />
                          )}
                        </Stack>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {basisFor === o.userOfferId && o.basis && (
                    <TableRow>
                      <TableCell colSpan={COLUMNS} sx={{ bgcolor: 'action.hover' }}>
                        <BasisList
                          title={`Na jakiej podstawie rekomendujemy ${o.externalId ?? `#${o.userOfferId}`}`}
                          basis={o.basis}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {skipped && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {skipped}
        </Typography>
      )}
      {result && !result.refusal && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {`Lista obejmuje zlecenia bez przypisanego pojazdu z załadunkiem ${withinDays(result.horizonDays)}. `}
          {accessNote
            ? `${accessNote} `
            : 'Dolot to szacunek: odległość w linii prostej × mnożnik terenowy, z dokładnością do ok. ±8%. '}
          {(withTolls
            ? 'Koszt dolotu to szacunek ze stawek spółki pojazdu (paliwo, kierowca, ciągnik) i opłat drogowych ' +
              'wg HERE; stawki w euro '
            : 'Koszt dolotu to szacunek ze stawek spółki pojazdu (paliwo, kierowca, ciągnik); stawki w euro ') +
            'przeliczamy kursem NBP z dnia załadunku zlecenia. Bez stawek albo kursu koszt jest nieznany. '}
          {'Marża to cena zlecenia w złotówkach (waluta obca tym samym kursem) minus koszt dolotu i trasy; ' +
            'to sygnał, kolejność zleceń się nie zmienia. '}
          {accessOriginText(result)}
        </Typography>
      )}
    </SectionCard>
  );
};

/** Stan panelu należy do rekordu — przejście na inny rekord zaczyna od zera (`recordScoped`). */
export const CarOrderSuggestions = recordScoped(CarOrderSuggestionsPanel);
